"use client";

import getPriceInfoFromProduct, {
  PriceType,
} from "@/utils/elevate/getPriceInfoFromProduct";
import { useMarket, useSiteInfo } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import type { CustomProduct } from "@apptus/esales-api";

function flattenProductFlags(product: CustomProduct) {
  const flags = new Set(
    [product.custom?.productFlags, product.custom?.variantFlags]
      .flat()
      .map((flag) => flag?.id),
  );
  return [...flags.values()].filter(Boolean) as string[];
}

export default function ProductCardBadge({
  product,
}: {
  product: CustomProduct;
}) {
  const t = useTranslate();
  const { market } = useMarket().state;
  const { campaigns } = useSiteInfo();

  const flags = flattenProductFlags(product);
  const campaign = campaigns?.find((c) => flags.includes(c?.commerceId ?? ""));

  const { priceType } = getPriceInfoFromProduct(product, market);
  const isCampaign =
    priceType === PriceType.campaign || priceType === PriceType.pricematch;

  const isNew = flags.includes("news");

  const campaignLabel = campaign?.campaignLabel;
  const fallbackCampaignLabel =
    isCampaign && !campaign ? t("campaignOrPricematch") : undefined;
  const newLabel = isNew ? t("new") : undefined;

  const labels = [campaignLabel, fallbackCampaignLabel, newLabel].filter(
    (label) => label && !label.startsWith("!"),
  );

  if (!labels.length) {
    return null;
  }

  return (
    <div className="absolute top-0 left-0 grid grid-flow-row justify-items-start gap-y-1 p-3">
      {labels.map((label) => (
        <span
          key={label}
          className="bg-background all-small-caps px-2 py-1 text-xs tracking-wider uppercase"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

ProductCardBadge.displayName = "ProductCardBadge";
